import { Fragment } from 'react';

import { DefaultComponentProps } from '../../types';
import { VerticalDivider } from '../Divider';

import {
  StepSubtitle,
  StepTitle,
  Steps,
  TextWrapper,
  WizardRadioButton,
  WizardRoot,
} from './styled';
import { WizardStepInfo } from './types';

export type WizardProps = DefaultComponentProps & {
  steps: WizardStepInfo[];
  activeStepId: string | undefined;
  completedStepIds: string[] | undefined;
  disabledStepIds?: string[] | undefined;
  onStepClick: (stepId: string) => void;
  backButton?: React.ReactNode;
  nextButton?: React.ReactNode;
  name?: string;
};

const Wizard: React.FC<WizardProps> = ({
  steps,
  activeStepId,
  completedStepIds,
  disabledStepIds,
  onStepClick,
  backButton,
  nextButton,
  name = 'wizard-step',
  ...rest
}) => {
  const activeStepIndex = steps.findIndex(item => item.id === activeStepId);

  const placeholder = <div aria-hidden="true" />;

  return (
    <WizardRoot {...rest}>
      {backButton || placeholder}

      <Steps>
        {steps.map((item, index) => {
          const isActive = item.id === activeStepId;
          const isPrev = index < activeStepIndex;
          const isCompleted = completedStepIds?.includes(item.id);

          const isFinished = (isPrev || isCompleted) && !isActive;

          const isDefaultDisabled = !isFinished && !isActive;
          const isDisabled =
            disabledStepIds?.includes(item.id) ?? isDefaultDisabled;

          const defaultSubtitle =
            index === steps.length - 1 ? 'Finish' : `Step ${index + 1}`;

          const subtitle = item.subtitle || defaultSubtitle;

          return (
            <Fragment key={item.id}>
              {index !== 0 ? <VerticalDivider /> : null}
              <WizardRadioButton
                aria-current={isActive}
                aria-label={item.title}
                checked={isActive}
                data-stepid={item.id} // locator for tests
                disabled={isDisabled}
                finished={isFinished}
                label={
                  <TextWrapper>
                    <StepSubtitle>{subtitle}</StepSubtitle>
                    <StepTitle $isActive={isActive}>{item.title}</StepTitle>
                  </TextWrapper>
                }
                name={name}
                onChange={() => undefined}
                onClick={() => {
                  onStepClick(item.id);
                }}
              />
            </Fragment>
          );
        })}
      </Steps>

      {nextButton || placeholder}
    </WizardRoot>
  );
};

export default Wizard;
